import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CmsService, LanguageService, ProductService, RoutingService } from '@spartacus/core';
import { combineLatest, first, Observable, Subscription, switchMap } from 'rxjs';
import { AccountOverviewService } from '../account-overview.service';
import { MakeAPaymentService } from "../../custom-make-a-payment/make-a-payment.service";
import { filter, map } from "rxjs/operators";
import { B2bUserService } from "../../shared/services/b2b-user.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { UserAccountFacade } from "@spartacus/user/account/root";
import { OrderHistoryService } from "../../order-history/order-history.service";
import { NumberTen } from "../../shared/utils/constants/global.constants";
import { NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { InvoiceUtils } from "../../shared/utils/invoice.utils";
import { AccountOverviewTabs, PreferencesTabs } from "../../shared/config/app.config";

@Component({
  selector: 'app-account-overview',
  templateUrl: './account-overview.component.html',
  styleUrls: ['./account-overview.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountOverviewComponent implements OnInit, OnDestroy {
  private readonly makeAPayment = inject(MakeAPaymentService);
  private readonly b2bUserService = inject(B2bUserService);
  private readonly user = inject(UserAccountFacade);
  private readonly ordersService = inject(OrderHistoryService);

  accountNumber: string = null;
  cmsResponse: any;
  productCodes: any;
  prodcutsInfo: any = [];
  userAccSub: Subscription;
  active: AccountOverviewTabs;
  private $b2bUnit: Observable<any>;
  ordersLoading = false;
  ordersList = signal([]);
  private ordersLoaded = false;
  readonly paymentList = signal([]);
  readonly creditList = signal([]);
  readonly statementsLoaded = signal(false);
  invoicesLoading = false;
  protected readonly AccountOverviewTabs = AccountOverviewTabs;

  constructor(
    private readonly acService: AccountOverviewService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: RoutingService,
    private readonly cmsData: CmsService,
    protected productService: ProductService,
    private readonly lang: LanguageService
  ) {
    this.$b2bUnit = toObservable(this.b2bUserService.b2bUser);
  }

  ngOnInit(): void {
    this.getInvoices();
    this.lang.getActive().subscribe((res) => {
      if (res) {
        this.prodcutsInfo = [];
      }
    });
    this.acService.getAccountId();
    this.acService.userAccountIdDetailsData$
      .pipe(
        first(Boolean)
      )
      .subscribe(
      (res) => {
        if (this.accountNumber === null) {
        this.accountNumber = res.uid;
          this.cdr.detectChanges();
          this.userAccSub?.unsubscribe();
        }
      }
    );
    this.cmsData
      .getComponentData('ProductMonographComponent')
      .subscribe((res) => {
        this.cmsResponse = res;
      });
  }

  navigateToAccountDetails() {
    void this.router.go(['/my-account/profile'], {
      queryParams: { activeTab: PreferencesTabs.ACCOUNT },
    });
  }

  ngOnDestroy() {
    this.userAccSub?.unsubscribe();
  }

  onNavChange($event: NgbNavChangeEvent<any>) {
    if ($event.nextId === AccountOverviewTabs.ORDERS && !this.ordersLoaded) {
      this.ordersLoaded = true;
      this.getOrders();
    }
  }

  onStatements(loaded: boolean): void {
    this.statementsLoaded.set(loaded);
  }

  private getOrders() {
    this.user.get()
      .pipe(
        first(Boolean),
        switchMap((user: any) => {
          const params = { baseSiteId: 'ss', userId: user.userName };
          const filters = { currentPage: 0, pageSize: 10 };
          this.ordersLoading = true;
          this.ordersService.getOrderHistory(params, filters);

          return this.ordersService.userData$
        }),
        filter(Boolean),
        map(ordersResponse => ordersResponse?.orders || [])
      ).subscribe({
      next: (orders: any) => {
        const ordersList = orders;

        for (const element of ordersList) {
          const dateAndTime = element.placed.split('T');
          element.placedDateObj = dateAndTime[0];
        }
        this.ordersList.set(ordersList.length >= NumberTen ? ordersList.slice(0, NumberTen) : ordersList.slice());

        this.ordersLoading = false;
      },
      error: (err: any) => {
        this.ordersLoading = false;
        console.info(err);
      },
    });
  }

  private getInvoices() {
    this.invoicesLoading = true;
    this.$b2bUnit
      .pipe(
        filter(Boolean),
        switchMap(b2bUser => {
          return this.getCreditsAndInvoices(b2bUser.uid)
        }),
        first(Boolean),
      )
      .subscribe(([openCredits, openInvoices]) => {
        this.paymentList.set(InvoiceUtils.getInvoiceTableDataV2(openInvoices.openInvoices));
        this.creditList.set(InvoiceUtils.getCreditsDataV2(openCredits.openInvoices));
        this.invoicesLoading = false;
      });
  }

  private getCreditsAndInvoices(userId: string): Observable<any[]> {
    return combineLatest([
      this.makeAPayment.getCachedCredits(userId),
      this.makeAPayment.getCachedInvoicesV2(userId)
    ]);
  }
}
